export interface RuleCondition {
  field: string;
  operator: string;
  value: unknown;
  dataType: 'string' | 'number' | 'boolean' | 'date' | 'array';
}

export interface RuleConditionGroup {
  operator: 'AND' | 'OR';
  rules?: RuleCondition[];
  groups?: RuleConditionGroup[];
}

export interface RuleAction {
  type: string;
  parameters: Record<string, unknown>;
}

export interface RuleSchedule {
  type: 'once' | 'daily' | 'weekly' | 'monthly' | 'yearly' | 'cron';
  expression?: string;
  startDate?: string;
  endDate?: string;
}

export interface RuleTestCase {
  name: string;
  input: Record<string, unknown>;
  expectedOutput: Record<string, unknown>;
}

export interface RuleMetadata {
  author?: string;
  approvedBy?: string;
  approvalDate?: string;
  testCases?: RuleTestCase[];
}

export interface Rule {
  id: string;
  name: string;
  description?: string;
  category: string;
  priority: number;
  isActive: boolean;
  conditions: RuleConditionGroup;
  actions: RuleAction[];
  triggers: string[];
  schedule?: RuleSchedule;
  version: string;
  tags?: string[];
  metadata?: RuleMetadata;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export interface RuleExecutionResult {
  ruleId: string;
  ruleName: string;
  passed: boolean;
  success: boolean;
  executionTime: number;
  error?: string;
  actions?: ActionResult[];
}

export interface ActionResult {
  type: string;
  success: boolean;
  result?: unknown;
  error?: string;
}

export interface RuleTestResult {
  ruleId: string;
  ruleName: string;
  input: Record<string, unknown>;
  result: RuleExecutionResult;
  timestamp: string;
}

export interface CreateRuleRequest {
  name: string;
  description?: string;
  category: string;
  priority: number;
  isActive: boolean;
  conditions: RuleConditionGroup;
  actions: RuleAction[];
  triggers: string[];
  schedule?: RuleSchedule;
  tags?: string[];
  metadata?: RuleMetadata;
}

export type UpdateRuleRequest = Partial<CreateRuleRequest>;

export interface RuleFilters {
  category?: string;
  isActive?: boolean;
  tag?: string;
}

export const RULE_CATEGORIES = [
  'Credit Scoring',
  'Risk Assessment',
  'Loan Approval',
  'Document Validation',
  'Pricing',
  'Compliance',
  'Notification',
  'Other'
] as const;

export const RULE_TRIGGERS = [
  'loan_application_created',
  'loan_application_updated',
  'client_created',
  'client_updated',
  'document_uploaded',
  'payment_received',
  'payment_missed',
  'form_submission_created',
  'form_submission_completed',
  'manual_trigger',
  'scheduled_trigger'
] as const;

export const CONDITION_OPERATORS = [
  'equals',
  'not_equals',
  'greater_than',
  'greater_than_or_equal',
  'less_than',
  'less_than_or_equal',
  'contains',
  'not_contains',
  'starts_with',
  'ends_with',
  'in',
  'not_in',
  'is_null',
  'is_not_null',
  'matches_regex'
] as const;

export const ACTION_TYPES = [
  'set_field',
  'calculate',
  'send_notification',
  'create_task',
  'trigger_workflow',
  'log_event',
  'call_api',
  'stop_processing'
] as const;

export const DATA_TYPES = [
  'string',
  'number',
  'boolean',
  'date',
  'array'
] as const;

export type RuleCategory = typeof RULE_CATEGORIES[number];
export type RuleTrigger = typeof RULE_TRIGGERS[number];
export type ConditionOperator = typeof CONDITION_OPERATORS[number];
export type ActionType = typeof ACTION_TYPES[number];
export type DataType = typeof DATA_TYPES[number];